import { useMemo } from 'react';
import { CSSObject } from '@emotion/react';
import { useResize } from '../../../hooks/useResize';
import { semantic_colors, Typography } from '../../../foundation';
import { ButtonProps } from '../../button';
import { StackProps } from '../../stack';
import {
  POPOVER_CELL_SIZE,
  BOTTOM_SHEET_CELL_SIZE,
  BOTTOM_SHEET_CAPTION_HEIGHT,
  MIN_SATE_PADDING_TOP,
} from './constants';
import { DisplayMode } from '../types';

interface DatePickerAttributes {
  maxHeight: number;
  panelSize: {
    width: number;
    height: number;
  };
  cellSize: number;
  caption: {
    kind: Typography;
    arrow: {
      size: number;
      width: number;
      height: number;
    };
    style?: CSSObject;
  };
  footer: {
    button: Partial<ButtonProps>;
    container: Partial<StackProps> & { style?: CSSObject };
  };
}

const datePickerAttr: Record<DisplayMode, Omit<DatePickerAttributes, 'maxHeight'>> = {
  'popover': {
    panelSize: {
      width: 84,
      height: 38,
    },
    cellSize: POPOVER_CELL_SIZE,
    caption: {
      kind: 'Body_14_Bold',
      arrow: {
        size: 12,
        width: 16,
        height: 24,
      },
      style: {
        marginBottom: '16px',
      },
    },
    footer: {
      button: {
        fill: false,
      },
      container: {
        width: 'auto',
        align: 'center',
      },
    },
  },
  'bottom-sheet': {
    panelSize: {
      width: 110,
      height: 70,
    },
    cellSize: BOTTOM_SHEET_CELL_SIZE,
    caption: {
      kind: 'Heading_17_Bold',
      arrow: {
        size: 16,
        width: 24,
        height: 24,
      },
      style: {
        position: 'fixed',
        top: '0',
        zIndex: 2,
        height: BOTTOM_SHEET_CAPTION_HEIGHT,
        backgroundColor: semantic_colors.background.surface,
      },
    },
    footer: {
      button: {
        fill: true,
        size: 'xlarge',
      },
      container: {
        width: '100%',
        p: 16,
        style: {
          flex: '0 0 auto',
          borderTop: `1px solid ${semantic_colors.border.primary}`,
        },
      },
    },
  },
  'calendar': {
    panelSize: {
      width: 84,
      height: 38,
    },
    cellSize: POPOVER_CELL_SIZE,
    caption: {
      kind: 'Body_14_Bold',
      arrow: {
        size: 12,
        width: 16,
        height: 24,
      },
      style: {
        marginBottom: '16px',
      },
    },
    footer: {
      button: {
        fill: false,
      },
      container: {
        width: 'auto',
        align: 'center',
      },
    },
  },
};

export function useDatePickerAttributes(displayMode: DisplayMode): DatePickerAttributes {
  const { windowWidth, windowHeight } = useResize(500);

  const cellSize = useMemo(() => {
    if (displayMode === 'popover' || displayMode === 'calendar') {
      return POPOVER_CELL_SIZE;
    }
    const tableMaxWidth = BOTTOM_SHEET_CELL_SIZE * 7;
    const {
      container: { p },
    } = datePickerAttr['bottom-sheet'].footer;
    const safePadding = (Number(p) * 2) / 7;
    return tableMaxWidth > windowWidth ? Math.floor(windowWidth / 7 - safePadding) : BOTTOM_SHEET_CELL_SIZE;
  }, [windowWidth, displayMode]);

  const maxHeight = useMemo(() => window.innerHeight - MIN_SATE_PADDING_TOP, [windowHeight]);

  return {
    ...datePickerAttr[displayMode],
    cellSize,
    maxHeight,
  };
}
